import { Injectable, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, merge, switchMap, take } from 'rxjs';
import { ActivityTracker } from '@app/core/session/activity-tracker';
import { OnboardingSessionStore } from '@app/core/session/onboarding-session.store';
import { SessionEndReason } from '@app/core/session/session-end-reason';
import { SessionExit } from '@app/core/session/session-exit';
import { SessionStatusPoller } from './session-status.poller';

/**
 * Cierre de sesión por inactividad y polling del estado del token. Vigila solo mientras hay una sesión activa
 * (RFC validado y token en curso): dos detectores independientes informan un motivo y `SessionExit` hace la salida.
 * - No se arranca ni se para a mano: todo lo que cierra la sesión (la salida, finalizar, un F5) apaga `sessionActive`,
 *   y el `switchMap` se desuscribe de los dos detectores.
 * - Los detectores no se conocen entre sí: por eso el polling nunca reinicia el contador de inactividad.
 * Se crea al arrancar la app (`app.config`).
 */
@Injectable({ providedIn: 'root' })
export class SessionLifecycle {
  private readonly store = inject(OnboardingSessionStore);
  private readonly activity = inject(ActivityTracker);
  private readonly poller = inject(SessionStatusPoller);
  private readonly exit = inject(SessionExit);

  /** El token de la sesión que hay que vigilar, o `null` si no hay sesión activa. */
  private readonly watchedToken = computed(() => (this.store.sessionActive() ? this.store.token() : null));

  constructor() {
    toObservable(this.watchedToken)
      .pipe(
        switchMap((token) => (token ? this.watch(token) : EMPTY)),
        takeUntilDestroyed(),
      )
      .subscribe((reason) => this.exit.end(reason));
  }

  private watch(token: string): Observable<SessionEndReason> {
    // take(1): con el primer motivo se detienen los dos detectores.
    return merge(this.activity.watch(), this.poller.watch(token, this.activity.backToForeground$)).pipe(take(1));
  }
}
